import { useEffect, useState, lazy, Suspense } from "react";
import { supabase } from "@/integrations/supabase/client";
import { ShieldCheck, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import TwoFactorSetup from "@/components/TwoFactorSetup";

const NotFound = lazy(() => import("@/pages/NotFound"));

type GateState = "loading" | "needs_enroll" | "needs_challenge" | "allowed" | "error";

/**
 * Opção 3 — Gate AAL2 obrigatório para o painel admin.
 *
 * Fluxo:
 *  1. Lista os fatores TOTP do usuário.
 *  2. Se NÃO houver TOTP verificado → renderiza o `TwoFactorSetup` (cadastro obrigatório).
 *  3. Se houver TOTP mas a sessão é AAL1 → pede o código de 6 dígitos antes de liberar.
 *  4. Se a sessão já é AAL2 → libera os children.
 *
 * Em caso de erro de rede repetido, camufla como 404 (mantém a postura de "rota oculta").
 */
const AdminAAL2Gate = ({ children }: { children: React.ReactNode }) => {
  const [state, setState] = useState<GateState>("loading");
  const [factorId, setFactorId] = useState<string>("");
  const [code, setCode] = useState("");
  const [verifying, setVerifying] = useState(false);

  const evaluate = async () => {
    try {
      const { data: factorsData, error: factorsErr } = await supabase.auth.mfa.listFactors();
      if (factorsErr) throw factorsErr;
      const verifiedTotp = factorsData?.totp?.find((f: any) => f.status === "verified");
      if (!verifiedTotp) {
        setState("needs_enroll");
        return;
      }
      setFactorId(verifiedTotp.id);
      const { data: aal, error: aalErr } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
      if (aalErr) throw aalErr;
      if (aal?.currentLevel === "aal2") {
        setState("allowed");
      } else {
        setState("needs_challenge");
      }
    } catch {
      setState("error");
    }
  };

  useEffect(() => {
    evaluate();
  }, []);

  const handleVerify = async () => {
    if (code.length !== 6) {
      toast.error("Digite o código de 6 dígitos");
      return;
    }
    setVerifying(true);
    try {
      const { data: allowed, error: rlErr } = await supabase.rpc("check_and_consume_rate_limit", {
        _action: "totp_verify",
        _max: 5,
        _window_seconds: 15 * 60,
      });
      if (rlErr) throw rlErr;
      if (!allowed) {
        toast.error("Muitas tentativas. Aguarde 15 minutos.");
        return;
      }
      const { data: challenge, error: chErr } = await supabase.auth.mfa.challenge({ factorId });
      if (chErr) throw chErr;
      const { error: vErr } = await supabase.auth.mfa.verify({
        factorId,
        challengeId: challenge.id,
        code,
      });
      if (vErr) throw vErr;
      toast.success("Verificação confirmada");
      setCode("");
      await evaluate();
    } catch (err: any) {
      toast.error(err?.message || "Código inválido");
    } finally {
      setVerifying(false);
    }
  };

  if (state === "loading") {
    return (
      <div className="min-h-dvh flex items-center justify-center bg-background">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (state === "error") {
    return (
      <Suspense fallback={<div className="min-h-dvh bg-background" />}>
        <NotFound />
      </Suspense>
    );
  }

  if (state === "needs_enroll") {
    return (
      <div className="min-h-dvh bg-background px-4 py-8 max-w-md mx-auto">
        <div className="flex items-center gap-2 mb-4">
          <ShieldCheck className="w-6 h-6 text-primary" />
          <h1 className="text-xl font-display font-semibold text-foreground">
            2FA obrigatório para administradores
          </h1>
        </div>
        <p className="text-sm text-muted-foreground font-body mb-6">
          O acesso ao painel administrativo exige autenticação em dois fatores.
          Configure agora um app autenticador para continuar.
        </p>
        <TwoFactorSetup onComplete={evaluate} />
      </div>
    );
  }

  if (state === "needs_challenge") {
    return (
      <div className="min-h-dvh bg-background px-4 py-8 max-w-md mx-auto flex flex-col justify-center">
        <div className="glass-card p-6 space-y-4">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-6 h-6 text-primary" />
            <h1 className="text-lg font-display font-semibold text-foreground">
              Confirmação em 2 etapas
            </h1>
          </div>
          <p className="text-sm text-muted-foreground font-body">
            Digite o código de 6 dígitos do seu app autenticador para acessar o painel.
          </p>
          <Input
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            placeholder="000000"
            maxLength={6}
            className="text-center text-2xl tracking-[0.5em] font-mono"
            inputMode="numeric"
            autoFocus
          />
          <Button
            onClick={handleVerify}
            disabled={verifying || code.length !== 6}
            className="w-full rounded-xl"
          >
            {verifying && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
            Confirmar
          </Button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default AdminAAL2Gate;
